import { change, Field, reduxForm, unregisterField, untouch } from "redux-form";
import Button from "components/atoms/Button";
import "./pickUp.scss";

import {
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useMediaQuery,
  Popper,
} from "@mui/material";
import { date, required, requiredAutoComplete } from "utils/validate.util";
import AutocompleteRedux from "components/molecules/AutocompleteRedux";
import DatePickerRedux from "components/molecules/DatePickerRedux";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import SelectRedux from "components/molecules/SelectRedux";
import OptionService from "utils/option.util";
import { mapAction } from "redux/slices/map/mapSlice";
import { bookingActions } from "redux/slices/booking";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PersistReduxForm from "components/organisms/PersistReduxForm";
import { useTranslation } from "react-i18next";
import MapWithPin from "components/templates/MapWithPin";
import Pin from "assets/icons/Pin";
import TimeDropdownRedux from "components/molecules/TimeDropdownRedux";
import theme from "theme";
import styled from "@emotion/styled";
import Input from "components/atoms/Input";
import {
  getMobileFieldTriggerProps,
  MobileSearchDrawerField,
  SharedMobileFieldDrawer,
} from "../shared/mobileSearchDrawer";

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: "50%",
  width: 18,
  height: 18,
  boxShadow: "inset 0 0 0 2px #999999",
  // border: "2px solid #999999", // Set border color to #999999 for unselected

  // backgroundColor: "#520a7607",
  "input:hover ~ &": {
    // backgroundColor: "#520a7616",
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#fff",
  boxShadow: "inset 0 0 0 5px #520A76",
  "&::before": {
    display: "block",
    width: 18,
    height: 18,
    content: '""',
  },
});

function BpRadio(props: any) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      sx={{ p: 0, position: "relative", bottom: "2px" }}
      {...props}
    />
  );
}

function PickUpForm({ handleSubmit }: any) {
  const form = "PickUpForm";
  const [formFocused, setFormFocused] = useState(false);
  const [differentReturnLocation, setDifferentReturnLocation] = useState(false);
  // Add state to track delivery drawer state
  const [deliveryDrawerState, setDeliveryDrawerState] = useState({
    pickup: false,
    destination: false,
  });

  const open = useAppSelector((state) => state.map.open);
  const mapConfirmed = useAppSelector((state) => state.map.close);
  const responsiveMap = useAppSelector((state) => state.map.destinationMap);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tab = useAppSelector((state) => state.booking.tab);
  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const show = useAppSelector((state) => state.map.show);
  const destination = useAppSelector(
    (state) => state.form?.[form]?.values?.destination,
  );
  const pickup = useAppSelector((state) => state.form?.[form]?.values?.pickup);
  const dateForm = useAppSelector((state) => state.form?.[form]?.values?.date);
  const timeValue = useAppSelector((state) => state.form?.[form]?.values?.time);
  const duration = useAppSelector(
    (state) => state.form?.[form]?.values?.duration,
  );

  // Then, create refs with those values
  const outsideForm = useRef<HTMLDivElement>(null);
  const pickupForm = useRef<HTMLFormElement>(null);
  const previousPickupRef = useRef(pickup);
  const previousDestinationRef = useRef(destination);
  const previousDurationRef = useRef(duration);
  const previousDateRef = useRef(dateForm);
  const previousTimeRef = useRef(timeValue);

  useEffect(() => {
    return () => {
      const fields = ["pickup", "destination"];
      fields.forEach((field) => {
        dispatch(unregisterField(form, field));
        dispatch(untouch(form, field));
      });
    };
  }, []); // <-- run only on unmount

  const [activeMobileField, setActiveMobileField] =
    useState<MobileSearchDrawerField | null>(null);

  const drawerState = useMemo(
    () => ({
      pickup: activeMobileField === "pickup",
      destination: activeMobileField === "destination",
      duration: activeMobileField === "duration",
      date: activeMobileField === "date",
      time: activeMobileField === "time",
    }),
    [activeMobileField],
  );

  const toggleDrawer = useCallback(
    (
      type: "pickup" | "destination" | "duration" | "date" | "time",
      state: boolean,
    ) => {
      setActiveMobileField(state ? type : null);
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setActiveMobileField(null);
  }, []);

  const handleFieldClick = (
    type: "pickup" | "destination" | "duration" | "date" | "time",
  ) => {
    if (isMobile) {
      toggleDrawer(type, true);
      if (type === "pickup") {
        dispatch(mapAction.setOpen(false));
        dispatch(mapAction.setShow(0));
        dispatch(mapAction.setdestinationMap(false));
      }
      if (type === "destination") {
        dispatch(mapAction.setOpen(false));
        dispatch(mapAction.setShow(1));
        dispatch(mapAction.setdestinationMap(false));
      }
    } else if (type === "pickup") {
      dispatch(mapAction.setOpen(false));
      dispatch(mapAction.setdestinationMap(false));
    } else if (type === "destination") {
      dispatch(mapAction.setOpen(false));
      dispatch(mapAction.setdestinationMap(false));
    }
  };

  const openMobileField = useCallback(
    (field: "pickup" | "destination" | "duration" | "date" | "time") => {
      handleFieldClick(field);
    },
    [isMobile, dispatch],
  );

  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const triggerMobileDrawer = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
      field: "pickup" | "destination" | "duration" | "date" | "time",
    ) => {
      if (!isMobile) return;
      event.preventDefault();
      event.stopPropagation();
      handleFieldClick(field);
    },
    [handleFieldClick, isMobile],
  );

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      // ✅ FIX: Check both PickUpForm and DeliveryForm drawer states
      const anyDrawerOpen =
        Boolean(activeMobileField) ||
        deliveryDrawerState.pickup ||
        deliveryDrawerState.destination;

      if (anyDrawerOpen) return;

      // Check if click is inside the map wrapper (including all its children like confirm button)
      const isInsideMap = mapWrapperRef.current?.contains(target);
      const isInsideForm = outsideForm.current?.contains(target);

      // Only close map if click is outside both form and map
      if (!isInsideForm && !isInsideMap) {
        dispatch(mapAction.setOpen(false));
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [activeMobileField, deliveryDrawerState, dispatch]); // ✅ Add deliveryDrawerState to dependencies

  useEffect(() => {
    if (isMobile) return;

    if (formFocused) {
      const destinationInput = document.getElementById("destination");
      if (destinationInput) {
        if (pickup?.details) {
          destinationInput.focus();
          destinationInput.click();
          closeDrawer();
        }
      }
    }
  }, [pickup, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    if (formFocused) {
      const durationInput = document.getElementById("duration");
      if (durationInput) {
        if (pickup?.details) {
          durationInput.focus();
          durationInput.click();
          closeDrawer();
        }
      }
    }
  }, [pickup, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    if (formFocused) {
      const dateInput = document.getElementById("date");
      if (dateInput) {
        if (destination?.details) {
          dateInput.focus();
          dateInput.click();
          closeDrawer();

          setTimeout(() => {
            let parent = dateInput.parentNode as HTMLElement;
            parent.classList.add("Mui-focused");
          }, 0);
        }
      }
    }
  }, [destination, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    if (formFocused) {
      const dateInput = document.getElementById("date");
      if (dateInput) {
        if (duration) {
          dateInput.focus();
          dateInput.click();

          setTimeout(() => {
            let parent = dateInput.parentNode as HTMLElement;
            parent.classList.add("Mui-focused");
          }, 0);
        }
      }
    }
  }, [duration, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    if (formFocused) {
      const dateInput = document.getElementById("date");
      const timeInput = document.getElementById("time");
      if (timeInput) {
        if (dateForm?.date) {
          timeInput.click();

          setTimeout(() => {
            let parentDiv = dateInput?.parentNode as HTMLElement;
            let parent = timeInput.parentNode as HTMLElement;
            parent.classList.add("Mui-focused");
            parentDiv.classList.remove("Mui-focused");
          }, 0);
        }
      }
    }
  }, [dateForm, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      previousPickupRef.current = pickup;
      previousDestinationRef.current = destination;
      previousDurationRef.current = duration;
      previousDateRef.current = dateForm;
      previousTimeRef.current = timeValue;
      return;
    }

    if (
      activeMobileField === "pickup" &&
      pickup?.details &&
      pickup !== previousPickupRef.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "destination" &&
      destination?.details &&
      destination !== previousDestinationRef.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "duration" &&
      duration &&
      duration !== previousDurationRef.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "date" &&
      dateForm?.date &&
      dateForm !== previousDateRef.current
    ) {
      closeDrawer();
    }

    if (
      activeMobileField === "time" &&
      timeValue &&
      timeValue !== previousTimeRef.current
    ) {
      closeDrawer();
    }

    previousPickupRef.current = pickup;
    previousDestinationRef.current = destination;
    previousDurationRef.current = duration;
    previousDateRef.current = dateForm;
    previousTimeRef.current = timeValue;
  }, [
    activeMobileField,
    closeDrawer,
    dateForm,
    destination,
    duration,
    isMobile,
    pickup,
    timeValue,
  ]);

  const prevMapOpenRef = useRef(open);
  useEffect(() => {
    if (isMobile && prevMapOpenRef.current === true && open === false && mapConfirmed) {
      if (activeMobileField === "pickup" || activeMobileField === "destination") {
        closeDrawer();
      }
    }
    prevMapOpenRef.current = open;
  }, [open, isMobile, activeMobileField, closeDrawer, mapConfirmed]);

  const handleTabChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    dispatch(bookingActions.setTab(value));
    dispatch(mapAction.setOpen(false));
    setActiveMobileField(null);
  };

  const pickupTriggerProps = getMobileFieldTriggerProps(
    isMobile,
    "pickup",
    openMobileField,
  );
  const destinationTriggerProps = getMobileFieldTriggerProps(
    isMobile,
    "destination",
    openMobileField,
  );
  const durationTriggerProps = getMobileFieldTriggerProps(
    isMobile,
    "duration",
    openMobileField,
  );
  const dateTriggerProps = getMobileFieldTriggerProps(
    isMobile,
    "date",
    openMobileField,
  );
  const timeTriggerProps = getMobileFieldTriggerProps(
    isMobile,
    "time",
    openMobileField,
  );

  const durationOptions = OptionService.getOptions_km(
    23,
    2,
    "",
    t("hours") || "hours",
    t("incl"),
    t("km"),
    {
      language: i18n.language,
      daysLabel: t("days") || "days",
    },
  );
  const selectedDurationLabel =
    durationOptions.find((option) => option.value === duration)?.label || "";
  const handleDurationSelect = useCallback(
    (value: string | number) => {
      dispatch(change(form, "duration", value));
      closeDrawer();
    },
    [closeDrawer, dispatch, form],
  );

  const mobileDrawerTitle = (() => {
    switch (activeMobileField) {
      case "pickup":
        return t("pickUp");
      case "destination":
        return t("destination");
      case "duration":
        return t("duration");
      case "date":
        return t("date");
      case "time":
        return t("time");
      default:
        return "";
    }
  })();

  const mobileDrawerSubtitle = (() => {
    switch (activeMobileField) {
      case "pickup":
        return pickup?.value || "";
      case "destination":
        return destination?.value || "";
      case "duration":
        return selectedDurationLabel;
      case "date":
        return dateForm?.date || "";
      case "time":
        return timeValue || "";
      default:
        return "";
    }
  })();

  const renderMobileDrawerContent = () => {
    switch (activeMobileField) {
      case "pickup":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: open ? "0 0 auto" : 1, maxHeight: open ? "60px" : undefined, minHeight: 0, display: "flex", overflow: "hidden" }}>
              <Field
                id="pickup_mobile_editor"
                name="pickup"
                label={t("pickUp")}
                validate={[requiredAutoComplete]}
                component={AutocompleteRedux}
                StartIcon={Pin}
                searchForm={true}
                renderDrawerContentOnly
                drawerInputAutoFocus
                AutocompleteProps={{
                  onSelectionComplete: closeDrawer,
                }}
              />
            </Box>
            {open && (
              <Box sx={{ flex: 1, minHeight: 0, borderTop: "1px solid #ECEBF2" }}>
                <MapWithPin />
              </Box>
            )}
          </Box>
        );
      case "destination":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: open ? "0 0 auto" : 1, maxHeight: open ? "60px" : undefined, minHeight: 0, display: "flex", overflow: "hidden" }}>
              <Field
                id="destination_mobile_editor"
                name="destination"
                label={t("destination")}
                validate={[requiredAutoComplete]}
                component={AutocompleteRedux}
                StartIcon={Pin}
                searchForm={true}
                renderDrawerContentOnly
                drawerInputAutoFocus
                AutocompleteProps={{
                  onSelectionComplete: closeDrawer,
                }}
              />
            </Box>
            {open && (
              <Box sx={{ flex: 1, minHeight: 0, borderTop: "1px solid #ECEBF2" }}>
                <MapWithPin />
              </Box>
            )}
          </Box>
        );
      case "duration":
        return (
          <Box sx={{ p: 2, overflowY: "auto", flex: 1, minHeight: 0 }}>
            {durationOptions.map((option) => {
              const isSelected = option.value === duration;
              return (
                <Button
                  key={String(option.value)}
                  fullWidth
                  variant={isSelected ? "contained" : "text"}
                  onClick={() => handleDurationSelect(option.value)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    mb: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: isSelected ? "#fff" : "#0A0A0E",
                    backgroundColor: isSelected ? "#520A76" : "#F7F7FA",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: isSelected ? "#520A76" : "#EFEFF5",
                    },
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Box>
        );
      case "date":
        return (
          <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Field
              id="date_mobile_editor"
              name="date"
              label={t("date")}
              validate={[date]}
              component={DatePickerRedux}
              DatePickerProps={{
                customLabelStyles: true,
                labelShrink: true,
                renderPickerOnly: true,
              }}
            />
          </Box>
        );
      case "time":
        return (
          <Box
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Field
              id="time_mobile_editor"
              name="time"
              label={t("time")}
              validate={[required]}
              component={TimeDropdownRedux}
              TimePickerProps={{
                customLabelStyles: true,
                labelShrink: true,
                renderPickerOnly: true,
                InputFieldProps: {
                  id: "time_mobile_editor",
                },
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ position: "relative", padding: "16px" }} ref={outsideForm}>
      {/* Radio Button Tabs */}
      <FormControl component="fieldset">
        <RadioGroup
          row
          value={tab.toString()}
          onChange={handleTabChange}
          sx={{
            flexWrap: "nowrap",
            gap: { xs: 0.5, sm: 1 }, // ← tighter gap on mobile
            "& .MuiFormControlLabel-root": {
              margin: 0,
              mt: 0,
              gap: 0.5,
              marginBottom: "16px",
              flexShrink: 0,
            },
          }}
        >
          <FormControlLabel
            value="0"
            control={<BpRadio />}
            label={
              <Box
                sx={{
                  fontSize: { xs: "11px", sm: "12px" }, // ← smaller on mobile
                  fontWeight: tab === 0 ? 600 : 400,
                  color: tab === 0 ? "#520A76" : "#999999",
                  mr: 0.5, // ← reduced margin
                }}
              >
                {t("transfer")}
              </Box>
            }
          />
          <FormControlLabel
            value="1"
            control={<BpRadio />}
            label={
              <Box
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: tab === 1 ? 600 : 400,
                  color: tab === 1 ? "#520A76" : "#999999",
                  mr: 0.5,
                }}
              >
                {t("hourly")}
              </Box>
            }
          />
          <FormControlLabel
            value="2"
            control={<BpRadio />}
            label={
              <Box
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: tab === 2 ? 600 : 400,
                  color: tab === 2 ? "#520A76" : "#999999",
                  mr: 0, // ← no margin on last
                }}
              >
                {t("citytoCity")}
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
      <>
        <form
          onSubmit={handleSubmit}
          ref={pickupForm}
          onFocus={() => setFormFocused(true)}
        >
          <Grid
            container={!isMobile}
            item={isMobile}
            spacing={isMobile ? 0 : 3}
            alignItems="center"
            justifyContent="center"
            xs={isMobile ? 12 : undefined}
            sm={isMobile ? 12 : undefined}
            sx={{ py: 0.5 }}
          >
            {/* Pickup Field */}
            <Grid item xs={12} sm={6} md={4}>
              <div
                className={
                  i18n.language === "en"
                    ? "pickUp__field"
                    : "pickUp__field arPickUp__field"
                }
                onClickCapture={(event) => triggerMobileDrawer(event, "pickup")}
                style={
                  open && show === 0
                    ? {
                        borderColor: "#520A76",
                        ...(i18n.language === "en"
                          ? { borderRight: "1px solid #520A76" }
                          : { borderLeft: "1px solid #520A76" }),
                      }
                    : {}
                }
              >
                <Field
                  onClick={() => handleFieldClick("pickup")}
                  id="pickup"
                  name="pickup"
                  label={t("pickUp")}
                  placeholder={t("enterLocation")}
                  validate={[requiredAutoComplete]}
                  component={AutocompleteRedux}
                  mobileTriggerProps={pickupTriggerProps}
                />
              </div>
            </Grid>
            {/* Destination / Duration Field */}
            {tab === 0 || tab === 2 ? (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                style={
                  i18n.language === "en"
                    ? { paddingLeft: 0 }
                    : { paddingRight: 0 }
                }
              >
                <div
                  className={
                    i18n.language === "en"
                      ? "destination__field"
                      : "destination__field arDestination__field"
                  }
                  onClickCapture={(event) =>
                    triggerMobileDrawer(event, "destination")
                  }
                  style={
                    open && show === 1
                      ? {
                          borderColor: "#520A76",
                          ...(i18n.language === "en"
                            ? { borderLeft: "1px solid #520A76" }
                            : { borderRight: "1px solid #520A76" }),
                        }
                      : {}
                  }
                >
                  <Field
                    onClick={() => handleFieldClick("destination")}
                    id="destination"
                    name="destination"
                    label={t("destination")}
                    placeholder={t("enterLocation")}
                    validate={[requiredAutoComplete]}
                    component={AutocompleteRedux}
                    mobileTriggerProps={destinationTriggerProps}
                  />
                </div>
              </Grid>
            ) : tab === 1 ? (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                style={
                  i18n.language === "en"
                    ? { paddingLeft: 0 }
                    : { paddingRight: 0 }
                }
              >
                <div
                  className={
                    i18n.language === "en"
                      ? "duration__field"
                      : "duration__field arDuration__field"
                  }
                  onClickCapture={(event) =>
                    triggerMobileDrawer(event, "duration")
                  }
                >
                  {isMobile ? (
                    <Input
                      label={t("duration")}
                      value={selectedDurationLabel}
                      placeholder={t("select_duration") || "Select duration"}
                      customLabelStyles
                      InputLabelProps={{ shrink: true }}
                      {...durationTriggerProps}
                    />
                  ) : (
                    <Field
                      id="duration"
                      name="duration"
                      label={t("duration")}
                      validate={[required]}
                      placeholder={t("select_duration")}
                      component={SelectRedux}
                      SelectProps={{
                        customLabelStyles: true,
                        labelShrink: true,
                        options: durationOptions,
                      }}
                    />
                  )}
                </div>
              </Grid>
            ) : null}

            {/* Date Field */}
            <Grid item xs={12} sm={6} md={1.5}>
              {" "}
              <div
                className={
                  i18n.language === "en"
                    ? "date__field"
                    : "date__field arDate__field"
                }
                onClickCapture={(event) => triggerMobileDrawer(event, "date")}
              >
                <Field
                  onClick={() => dispatch(mapAction.setOpen(false))}
                  id="date"
                  name="date"
                  label={t("date")}
                  validate={[date]}
                  disablePast
                  component={DatePickerRedux}
                  DatePickerProps={{
                    customLabelStyles: true,
                    labelShrink: true,
                    disableInternalOpen: isMobile,
                    triggerOnlyProps: dateTriggerProps,
                  }}
                />
              </div>
            </Grid>

            {/* Time Field */}
            <Grid
              item
              xs={12}
              sm={6}
              md={1.5}
              style={
                i18n.language === "en"
                  ? { paddingLeft: 0 }
                  : { paddingRight: 0 }
              }
            >
              <div
                className={
                  i18n.language === "en"
                    ? "time__field"
                    : "time__field arTime__field"
                }
                onClickCapture={(event) => triggerMobileDrawer(event, "time")}
              >
                <Field
                  onClick={() => dispatch(mapAction.setOpen(false))}
                  id="time"
                  name="time"
                  label={t("time")}
                  validate={[required]}
                  component={TimeDropdownRedux}
                  TimePickerProps={{
                    customLabelStyles: true,
                    labelShrink: true,
                    disableInternalOpen: isMobile,
                    triggerOnlyProps: timeTriggerProps,
                    InputFieldProps: {
                      id: "time",
                    },
                  }}
                />
              </div>
            </Grid>

            {/* Search Button */}
            <Grid
              item
              xs={12}
              sm={6}
              md={1}
              display="flex"
              justifyContent="center"
              style={{ paddingLeft: 8 }}
            >
              <Button
                sx={{
                  width: "100%",
                  maxWidth: "152px",
                  height: "65px",
                  borderRadius: "10px",
                  backgroundColor: "#520A76",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "16px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#6A1B9A" },
                }}
                variant="contained"
                type="submit"
              >
                {t("search")}
              </Button>
            </Grid>
          </Grid>
        </form>
        {isMobile && activeMobileField && (
          <SharedMobileFieldDrawer
            open={Boolean(activeMobileField)}
            onClose={closeDrawer}
            title={mobileDrawerTitle}
            subtitle={mobileDrawerSubtitle}
          >
            {renderMobileDrawerContent()}
          </SharedMobileFieldDrawer>
        )}
        {/* Map Component */}
        <Popper
          open={!!open && !isMobile}
          anchorEl={outsideForm.current} // anchor to the form container
          placement={i18n.language === "en" ? "bottom-start" : "bottom-end"}
          style={{ zIndex: 1600 }} // higher than menus
          modifiers={[
            { name: "offset", options: { offset: [15, -10] } },
            { name: "preventOverflow", options: { padding: 16 } },
          ]}
        >
          <Box
            ref={mapWrapperRef}
            sx={{
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
              width: isMobile ? "95vw" : 850,
              height: isMobile ? "70vh" : 368,
              overflow: "hidden",
            }}
          >
            <MapWithPin />
          </Box>
        </Popper>
      </>
      {/* )} */}
      <PersistReduxForm form={form} />
    </div>
  );
}

export default reduxForm({
  form: "PickUpForm",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(PickUpForm);
